/**
 * Page View Tracking Hook
 *
 * Automatically tracks page views on route changes using React Router's useLocation hook.
 * Extracts page title from route path and calls trackPageView from AnalyticsContext.
 *
 * Validates: Requirements 10.1, 10.2, 10.3, 10.4, 10.5, 10.6, 12.3
 */

import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

import { useAnalytics } from '../contexts/AnalyticsContext'

/**
 * Custom hook that automatically tracks page views on route changes.
 *
 * This hook uses React Router's useLocation to detect route changes and
 * calls trackPageView from the AnalyticsContext with the current path and title.
 *
 * The page title is extracted from the route path by taking the first segment
 * after the leading slash, or 'home' if the path is the root.
 *
 * Example:
 * - '/offices' -> title: 'offices'
 * - '/book' -> title: 'book'
 * - '/' -> title: 'home'
 *
 * Usage:
 * ```tsx
 * function App() {
 *   usePageViewTracking()
 *   return <Routes>...</Routes>
 * }
 * ```
 *
 * Validates: Requirements 10.1, 10.2, 10.3, 10.4, 10.5, 10.6, 12.3
 */
export const usePageViewTracking = (): void => {
  const location = useLocation()
  const { trackPageView } = useAnalytics()

  useEffect(() => {
    // Extract page title from route path
    // For '/' return 'home', for '/offices' return 'offices', etc.
    const pathSegments = location.pathname.split('/').filter(Boolean)
    const title = pathSegments.length > 0 ? pathSegments[0] : 'home'

    // Track the page view with full path and extracted title
    trackPageView(location.pathname, title)
  }, [location, trackPageView])
}
